/**
 * Oct 20 field test (Issues 03A / 03B) — which vehicle actions a FOH Workboard card offers.
 *
 * Pure. The SERVER decides every act (move_vehicle_location, mark_completed_ticket_picked_up,
 * mark_picked_up_incomplete_if_quiescent); this only decides what is worth showing, and on which ticket.
 *
 *   Move Vehicle                    — any live, present vehicle (not picked up, not deleted). Acts on the LIVE
 *                                     segment (the day that holds the vehicle's location).
 *   Customer Picked Up              — a completed repair awaiting pickup. Acts on the repair's OWNER ticket
 *                                     (a multi-day repair leaves once, from its first ticket).
 *   Picked Up — Work Incomplete     — a live, unfinished repair. Acts on the live ticket (the day that holds the
 *                                     vehicle). Oct 20 addendum L3: offered on a multi-day (rollover) repair too —
 *                                     the authority withdraws the unworked future days and keeps the history.
 */
import type { LogicalRepairOrder } from './workboardModel';

export interface WorkboardActionPermissions {
  /** usePermissions().canRelocateVehicles */
  canRelocateVehicles: boolean;
  /** usePermissions().canReleaseCompletedVehicle */
  canReleaseCompletedVehicle: boolean;
  /** usePermissions().canEditTickets — the incomplete pickup keeps ticket-edit law (no DISPATCHER). */
  canEditTickets: boolean;
}

export interface WorkboardVehicleActions {
  move: { ticketId: string } | null;
  pickedUp: { ticketId: string } | null;
  pickedUpIncomplete: { ticketId: string } | null;
  /** Why "Picked Up — Work Incomplete" is not offered for an otherwise eligible card, when that is the reason. */
  incompleteUnavailableReason: string | null;
}

const NONE: WorkboardVehicleActions = { move: null, pickedUp: null, pickedUpIncomplete: null, incompleteUnavailableReason: null };

export function workboardVehicleActions(
  ro: Pick<LogicalRepairOrder, 'id' | 'owner' | 'live' | 'members' | 'productionState'>,
  perms: WorkboardActionPermissions,
): WorkboardVehicleActions {
  const s = ro.productionState;
  if (s === 'picked_up' || s === 'deleted' || s === 'archived' || s === 'estimate_prospect') return NONE;

  const completed = s === 'completed_awaiting_pickup';

  const move = perms.canRelocateVehicles ? { ticketId: ro.live.id } : null;
  const pickedUp = completed && perms.canReleaseCompletedVehicle ? { ticketId: ro.id } : null;
  const incompleteEligible = !completed && perms.canEditTickets;
  // Oct 20 addendum (L3): a multi-day repair may leave incomplete too; its future days are released server-side.
  const pickedUpIncomplete = incompleteEligible ? { ticketId: ro.live.id } : null;
  return { move, pickedUp, pickedUpIncomplete, incompleteUnavailableReason: null };
}

/** Preset reasons for "Picked Up — Work Incomplete" (FD-06). "Other" requires the person's own words. */
export const INCOMPLETE_PICKUP_REASONS = [
  { value: 'declined', label: 'Customer declined the remaining work' },
  { value: 'parts', label: 'Waiting on parts' },
  { value: 'return_later', label: 'Customer will return another day' },
  { value: 'needs_vehicle', label: 'Customer needs the vehicle in the meantime' },
  { value: 'other', label: 'Other' },
] as const;
export type IncompletePickupReasonCode = (typeof INCOMPLETE_PICKUP_REASONS)[number]['value'];

/**
 * The reason text the server records: the preset label, plus the person's note ("Other" requires a note).
 * Null = not yet a valid reason (the confirm stays disabled and says why).
 */
export function incompletePickupReasonText(code: IncompletePickupReasonCode | null, note: string): string | null {
  const n = note.trim();
  if (!code) return null;
  if (code === 'other') return n.length > 0 ? n : null;
  const label = INCOMPLETE_PICKUP_REASONS.find((r) => r.value === code)?.label ?? null;
  if (!label) return null;
  return n.length > 0 ? `${label} — ${n}` : label;
}

/** YYYY-MM-DD today in the shop's time zone (the earliest valid expected return date). */
export function shopToday(timeZone: string, now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);
}

/** An optional expected return date (FD-07): empty is valid; a value must be a real date, today or later. */
export function expectedReturnDateProblem(value: string, today: string): string | null {
  const v = value.trim();
  if (!v) return null;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(v) || Number.isNaN(new Date(`${v}T00:00:00Z`).getTime())) return 'Enter a valid date.';
  if (v < today) return 'The expected return date cannot be in the past.';
  return null;
}
